import type { EPersistKey } from '~/types/persist';

import { useUserActionHandlers } from '~/hooks/useUserActionHandlers';
import { useCallback, useEffect, useMemo, useState } from 'react';

interface IPersistedStateProps<T> {
  /**
   * The key in object value of user actions.
   */
  key: string;
  /**
   * The default value to use if no stored value exists.
   */
  defaultValue: T;
  extractValue?: (value: string) => T;
  /**
   * The key to local storage.
   */
  persistKey: EPersistKey;
}

type IPersistedStateReturn<T> = [T | undefined, (value: T | undefined) => void];

const cache = new Map<string, any>();
/**
 * Custom hook to manage persisted state with user actions.
 * @param key - The key to store the value in actions
 * @param defaultValue - The default value to use if no stored value exists
 * @param extractValue - Optional - if value is `boolean`, required to set `JSON.parse`
 * @returns A tuple of the state value and a setter function
 */
export const usePersistedState = <T>(props: IPersistedStateProps<T>): IPersistedStateReturn<T> => {
  const { key, defaultValue, extractValue = (value) => value as T, persistKey } = props;
  const { actions, setUserActionBy } = useUserActionHandlers();

  const [state, setState] = useState<T | undefined>();

  // TODO: Hotfix
  // TODO: Fix and refactor at sprint 90
  const keyWithPersistKey = useMemo(() => `${persistKey}-${key}`, [persistKey, key]);

  const handleSetState = useCallback(
    (value: T | undefined) => {
      setState(value);
      cache.set(keyWithPersistKey, value);
    },
    [keyWithPersistKey],
  );

  const setPersistedState = (value: T | undefined) => {
    const stringifiedValue = typeof value === 'string' ? value : JSON.stringify(value);
    setUserActionBy(keyWithPersistKey, stringifiedValue);
    handleSetState(value);
  };

  useEffect(() => {
    if (typeof actions === 'undefined') return;
    if (state) return;
    if (cache.has(keyWithPersistKey)) {
      handleSetState(cache.get(keyWithPersistKey));
      return;
    }

    const storedValue = actions[keyWithPersistKey];
    if (storedValue) {
      try {
        const parsedValue = extractValue(storedValue);
        handleSetState(parsedValue);
      } catch {
        handleSetState(defaultValue);
      }
    } else {
      handleSetState(defaultValue);
    }
  }, [actions, defaultValue, extractValue, keyWithPersistKey, state, handleSetState]);

  return [state, setPersistedState];
};
